import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getGoogleSignInUrl } from '@/lib/auth/authService';
import { useAuth } from '@/lib/auth/AuthContext';
import { SEO } from '@/components/SEO';

export function LoginPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleGoogleSignIn = () => {
    window.location.href = getGoogleSignInUrl('/login/callback');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950">
      <SEO title="Sign In" />

      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 left-1/2 h-[500px] w-[800px] -translate-x-1/2 rounded-full bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20 blur-3xl" />
        <div className="absolute top-20 left-20 h-72 w-72 rounded-full bg-indigo-500/10 blur-3xl" />
        <div className="absolute top-40 right-20 h-72 w-72 rounded-full bg-purple-500/10 blur-3xl" />
      </div>

      <div className="relative max-w-md w-full space-y-8 p-8">
        {/* Logo and branding */}
        <div className="text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/25">
            <svg className="h-8 w-8 text-white" fill="currentColor" viewBox="0 0 24 24" role="img" aria-label="Microphone">
              <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z"/>
              <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
            </svg>
          </div>
          <h1 className="bg-gradient-to-r from-white via-gray-100 to-gray-300 bg-clip-text text-3xl font-bold tracking-tight text-transparent">
            Kaster Media
          </h1>
          <p className="mt-2 text-gray-400">
            Your personal podcast feed
          </p>
        </div>

        {/* Sign in card */}
        <div className="rounded-2xl border border-gray-800 bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-8 backdrop-blur-sm">
          <div className="mb-6 text-center">
            <h2 className="text-xl font-semibold text-white">
              Welcome
            </h2>
            <p className="mt-1 text-sm text-gray-400">
              Sign in to access your personalized dashboard
            </p>
          </div>

          <button
            type="button"
            onClick={handleGoogleSignIn}
            className="group w-full flex items-center justify-center gap-3 rounded-xl border border-gray-700 bg-gray-800/50 px-4 py-3.5 font-medium text-white transition-all hover:border-gray-600 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" role="img" aria-label="Google logo">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Sign in with Google
          </button>

          <p className="mt-6 text-center text-xs text-gray-500">
            By signing in, you agree to our terms of service
          </p>
        </div>
      </div>
    </div>
  );
}
